import { Box } from '@mui/material';
import { ReferenceManyField, TopToolbar, useGetRecordId, useRecordContext } from 'react-admin';

import { GetB2bOrdersOneResponse, OdOrderStatus } from '@Common/types/b2b/orders';
import { SavingsFundsRequestDetails } from '@Common/types/bo/b2b/savings_funds_requests';

import { b2bOrderStatusChoices } from '@sh4res/bo-common/choices/orders';
import { DatagridTyped, Pagination, ServerExportButton } from '@sh4res/bo-common/components';
import { CurrencyField, IdField, SelectChipField } from '@sh4res/bo-common/fields';
import { ResourceBasePath } from '@sh4res/bo-common/hooks';
import type { ColumnList } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type OrderItem = Pick<
  GetB2bOrdersOneResponse,
  'id' | 'user_currency_amount' | 'tax_amount' | 'status'
> & {
  total_gross_amount: number;
} & Partial<Pick<GetB2bOrdersOneResponse, 'matched_contribution_order_id'>>;

const testId = 'BoExternalB2bSavingsFundRequestsLinkedOrdersList';

const displayedColumns: (keyof OrderItem)[] = isBoExternal()
  ? ['total_gross_amount', 'user_currency_amount', 'tax_amount']
  : [
      'id',
      'total_gross_amount',
      'user_currency_amount',
      'tax_amount',
      'matched_contribution_order_id',
      'status',
    ];

export const LinkedOrdersList = ({ resourceBasePath }: { resourceBasePath: ResourceBasePath }) => {
  const showOrderIds = !isBoExternal();
  const recordId = useGetRecordId() as number;
  const record = useRecordContext<SavingsFundsRequestDetails>();
  const isCcbWithdrawal = record?.type === 'ccb_withdrawal';

  const columnRegistry = {
    ...(showOrderIds
      ? {
          id: (
            <IdField
              reference={`${resourceBasePath}orders`}
              label="resources.bo_common.b2b.savings_funds_requests.orders.fields.id"
              data-testid={`${testId}_IdField`}
            />
          ),
        }
      : {}),
    user_currency_amount: (
      <CurrencyField
        currency="user_currency"
        label="resources.bo_common.b2b.savings_funds_requests.orders.fields.user_currency_amount"
        testId={`${testId}_UserCurrencyAmountField`}
      />
    ),
    total_gross_amount: (
      <CurrencyField
        currency="user_currency"
        label="resources.bo_common.b2b.savings_funds_requests.orders.fields.total_gross_amount"
        testId={`${testId}_TotalGrossAmountField`}
      />
    ),
    tax_amount: (
      <CurrencyField
        currency="user_currency"
        label="resources.bo_common.b2b.savings_funds_requests.orders.fields.tax_amount"
        testId={`${testId}_TaxAmountField`}
      />
    ),
    ...(showOrderIds
      ? {
          matched_contribution_order_id: (
            <IdField
              reference={`${resourceBasePath}orders`}
              label="resources.bo_common.b2b.savings_funds_requests.orders.fields.matched_contribution_order_id"
              data-testid={`${testId}_MatchedContributionIdField`}
            />
          ),
        }
      : {}),
    status: (
      <SelectChipField
        choices={b2bOrderStatusChoices}
        label="resources.bo_common.b2b.savings_funds_requests.orders.fields.status"
        data-testid={`${testId}_StatusField`}
      />
    ),
  };

  const nonCancelledStatuses: OdOrderStatus[] = [
    'pending',
    'filled',
    'shares_new',
    'pending_funds',
    'shares_pending_arbitrage',
    'shares_pending_approval',
    'shares_pending_company',
    'shares_pending_campaign',
    'shares_pending_reallocation',
    'shares_pending_cutoff_period',
    'shares_pending_blocked_instrument',
    'shares_pending_triggered_order',
    'shares_pending_scheduling',
  ];

  return (
    <ReferenceManyField
      data-testid={`${testId}_OrdersList`}
      reference={`${resourceBasePath}orders`}
      filter={{
        // Cancelling a CCB withdrawal SFR acts on its orders, so cancelled ones must stay visible
        ...(!isCcbWithdrawal && { status: nonCancelledStatuses }),
        is_managed: false,
      }}
      target="savings_funds_requests_id"
      perPage={10}
      pagination={<Pagination />}
      sort={{ field: 'created_at', order: 'DESC' }}>
      <Box
        sx={{
          padding: '0 15px',
        }}>
        <TopToolbar>
          {/* Covers Libre orders as well as managed contributions (Piloté/Profilé) */}
          <ServerExportButton
            resource={`${resourceBasePath}orders`}
            label="resources.bo_common.b2b.savings_funds_requests.orders.export.button"
            getBody={() => ({
              kind: 'savings_funds_request_orders',
              savings_funds_request_id: recordId,
            })}
            data-testid={`${testId}_ExportButton`}
          />
        </TopToolbar>
      </Box>
      <DatagridTyped<OrderItem>
        rowClick={showOrderIds ? 'show' : false}
        sortableColumns={[]}
        columnRegistry={columnRegistry as ColumnList<OrderItem>}
        displayedColumns={displayedColumns}
        bulkActionButtons={false}
        showCard={true}
      />
    </ReferenceManyField>
  );
};
